import { Controller } from "@hotwired/stimulus";

const CSVBOX_SCRIPT_URL = "https://js.csvbox.io/script.js";
const CSVBOX_SCRIPT_SELECTOR = 'script[src*="csvbox.io"]';
const URL_PARAM_IMPORT_SUCCESS = "import_success";
const URL_PARAM_IMPORTED_ROWS = "imported_rows_count";
const DEFAULT_SUCCESS_MESSAGE = "Import successful!";
const ICON_HTML_SUCCESS = '<i class="material-icons" style="vertical-align: middle; margin-right: 4px; font-size: 1.2em;">check_circle</i>';

export default class extends Controller {
  static targets = ["successMessage"];
  static values = {
    key: String,
    userId: String,
    assessmentId: String
  }

  csvBoxImporter = null;

  async connect() {
    this.checkForSuccessMessage();
    if (window.CSVBoxImporter) {
      this.initializeCSVBoxImporter();
    } else {
      try {
        await this.loadCSVBoxScript();
        this.initializeCSVBoxImporter();
      } catch (error) {
        console.error("Failed to load CSVBox script:", error);
      }
    }
  }

  disconnect() {
    if (this.csvBoxImporter) {
      this.csvBoxImporter = null; // Cleanup CSVBox instance
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.style.display = 'none';
    }
  }

  loadCSVBoxScript() {
    return new Promise((resolve, reject) => {
      if (document.querySelector(CSVBOX_SCRIPT_SELECTOR)) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = CSVBOX_SCRIPT_URL;
      script.onload = resolve;
      script.onerror = () => reject(new Error("Failed to load CSVBox script"));
      document.head.appendChild(script);
    });
  }

  initializeCSVBoxImporter() {
    if (this.csvBoxImporter) return;

    const callback = (result, data) => {
      if (result) {
        this._handleImportSuccess(data);
      }
    };
    // Ensure data values are used here
    this.csvBoxImporter = new CSVBoxImporter(this.keyValue, {}, callback);
    this.csvBoxImporter.setUser({
      user_id: this.userIdValue,
      assessment_id: this.assessmentIdValue
    });
  }

  _getImportedRowCount(data) {
    if (!data) return null;
    if (typeof data.row_success === 'number') return data.row_success;
    if (typeof data.rowCount === 'number') return data.rowCount; // Fallback
    if (typeof data.imported_rows === 'number') return data.imported_rows; // Fallback
    if (data.summary && typeof data.summary.successful_rows === 'number') return data.summary.successful_rows; // Fallback
    return null;
  }

  _handleImportSuccess(data) {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(URL_PARAM_IMPORT_SUCCESS, "true");
    const importedRowCount = this._getImportedRowCount(data);
    if (importedRowCount !== null) {
      currentUrl.searchParams.set(URL_PARAM_IMPORTED_ROWS, importedRowCount);
    }
    window.location.href = currentUrl.toString();
  }

  openModal(event) {
    event.preventDefault();
    if (this.csvBoxImporter) {
      this.csvBoxImporter.openModal();
    }
  }

  checkForSuccessMessage() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has(URL_PARAM_IMPORT_SUCCESS)) {
      const rowCount = urlParams.get(URL_PARAM_IMPORTED_ROWS);
      this._displaySuccessFeedback(rowCount);
      this._cleanUrlParams(urlParams);
    }
  }

  _displaySuccessFeedback(rowCount) {
    let message = DEFAULT_SUCCESS_MESSAGE;
    if (rowCount) {
      message = `${rowCount} rows imported successfully!`;
    }

    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.innerHTML = `${ICON_HTML_SUCCESS} ${message}`;
      this.successMessageTarget.style.display = 'flex';
    } else {
      alert(`${ICON_HTML_SUCCESS} ${message}`); // Consider if icon is desired in alert
    }
  }

  _cleanUrlParams() {
    const newUrl = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
  }
}